import { useCallback, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import AdminShell from "../components/AdminShell";
import { getAdminUsers, isApiConfigured } from "../apiClient";

function formatWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function signupTypeLabel(signupType) {
  return signupType === "hiring-manager" ? "Hiring manager" : "Applicant";
}

function yesNo(value) {
  return value ? "Yes" : "No";
}

export default function AdminUsersPage({ currentUser, authToken, onSignOut }) {
  const [emailQuery, setEmailQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [signupTypeFilter, setSignupTypeFilter] = useState("");
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasSearched, setHasSearched] = useState(false);

  const runSearch = useCallback(
    async (email, signupType) => {
      const t = String(authToken || "").trim();
      const q = String(email || "").trim();
      if (!t || !isApiConfigured()) {
        setError("Not signed in.");
        setUsers([]);
        setTotal(0);
        return;
      }
      if (!q) {
        setError("Enter an email address to search.");
        setUsers([]);
        setTotal(0);
        setHasSearched(false);
        return;
      }

      setLoading(true);
      setError("");
      setHasSearched(true);
      try {
        const data = await getAdminUsers(t, {
          email: q,
          signupType: signupType || undefined,
          limit: 50,
        });
        setUsers(Array.isArray(data.users) ? data.users : []);
        setTotal(Number(data.total) || 0);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not search users.");
        setUsers([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [authToken]
  );

  if (currentUser?.role !== "admin") {
    return <Navigate to="/admin/signin" replace />;
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    setSubmittedQuery(emailQuery.trim());
    void runSearch(emailQuery, signupTypeFilter);
  };

  return (
    <AdminShell currentUser={currentUser} onSignOut={onSignOut}>
      <Link to="/admin" className="jw-admin__back">
        ← Back to home
      </Link>
      <h1 className="jw-admin__title">User management</h1>
      <p className="jw-admin__lede">Search product accounts by email address.</p>

      <form className="jw-admin-users-search" onSubmit={handleSubmit}>
        <label className="jw-admin-users-search__field" htmlFor="admin-user-email-search">
          <span className="jw-admin-users-search__label">Email</span>
          <input
            id="admin-user-email-search"
            type="search"
            className="jw-admin-users-search__input"
            placeholder="name@company.com"
            value={emailQuery}
            onChange={(e) => setEmailQuery(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        <label className="jw-admin-users-search__field" htmlFor="admin-user-type-filter">
          <span className="jw-admin-users-search__label">Account type</span>
          <select
            id="admin-user-type-filter"
            className="jw-admin__filter-select"
            value={signupTypeFilter}
            onChange={(e) => setSignupTypeFilter(e.target.value)}
          >
            <option value="">All types</option>
            <option value="applicant">Applicant</option>
            <option value="hiring-manager">Hiring manager</option>
          </select>
        </label>
        <button type="submit" className="jw-admin__refresh" disabled={loading}>
          {loading ? "Searching…" : "Search"}
        </button>
      </form>

      {error ? (
        <p className="jw-admin__error" role="alert">
          {error}
        </p>
      ) : null}

      {!loading && !hasSearched && !error ? (
        <p className="jw-admin__muted">Enter an email address and hit Search to find users.</p>
      ) : null}

      {!loading && hasSearched && users.length === 0 && !error ? (
        <p className="jw-admin__muted">
          No users match{submittedQuery ? ` “${submittedQuery}”` : " that email"}.
        </p>
      ) : null}

      {!loading && users.length > 0 ? (
        <>
          <p className="jw-admin-users-results-summary">
            {total === 1 ? "1 user found" : `${total} users found`}
            {total > users.length ? ` (showing first ${users.length})` : ""}
          </p>
          <div className="jw-admin-rates__table-wrap">
            <table className="jw-admin-rates__table jw-admin-users-table">
              <thead>
                <tr>
                  <th scope="col">Email</th>
                  <th scope="col">Name</th>
                  <th scope="col">Type</th>
                  <th scope="col">Verified</th>
                  <th scope="col">Operating country</th>
                  <th scope="col">Company</th>
                  <th scope="col">Joined</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <Link to={`/admin/users/${user.id}`} className="jw-admin-user-email-link">
                        <code>{user.email}</code>
                      </Link>
                    </td>
                    <td>{user.fullName || "—"}</td>
                    <td>{signupTypeLabel(user.signupType)}</td>
                    <td>
                      Email {yesNo(user.emailVerified)}
                      <br />
                      Phone {yesNo(user.phoneVerified)}
                    </td>
                    <td>{user.countryOfResidence || "—"}</td>
                    <td>{user.companyName || "—"}</td>
                    <td>
                      <time dateTime={user.createdAt}>{formatWhen(user.createdAt)}</time>
                    </td>
                    <td>
                      <Link to={`/admin/users/${user.id}`} className="jw-admin-user-view-link">
                        View user
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </AdminShell>
  );
}
